import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
} from '@angular/core';
import { AsyncPipe, UpperCasePipe } from '@angular/common';
import {
  GoogleMap,
  MapAdvancedMarker,
  MapDirectionsRenderer,
} from '@angular/google-maps';
import {
  TuiButtonModule,
  TuiDialogContext,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import { TuiBadgeModule, TuiInputModule } from '@taiga-ui/kit';
import { TuiLetModule } from '@taiga-ui/cdk';
import { BehaviorSubject, map, Observable, shareReplay, Subject } from 'rxjs';
import { FormBuilder, Validators } from '@angular/forms';
import { POLYMORPHEUS_CONTEXT } from '@tinkoff/ng-polymorpheus';
import {
  GoogleMapService,
  RoutePartDetails,
} from '@shared/services/google-map.service';
import { PointWithLabel } from '../../../../types/location';

export type RoutePreviewInputData = {
  routes: PointWithLabel[];
};

@Component({
  selector: 'app-route-preview',
  standalone: true,
  imports: [
    AsyncPipe,
    GoogleMap,
    MapAdvancedMarker,
    TuiButtonModule,
    TuiInputModule,
    TuiLetModule,
    TuiTextfieldControllerModule,
    MapDirectionsRenderer,
    UpperCasePipe,
    TuiBadgeModule,
  ],
  providers: [GoogleMapService],
  templateUrl: './route-preview.component.html',
  styleUrl: './route-preview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoutePreviewComponent implements OnInit {
  search$ = new Subject<void>();
  search = this.fb.control('', Validators.required);
  options$ = new BehaviorSubject<Nullable<google.maps.MapOptions>>(
    this.googleMapService.getInitialMapOptions(),
  );

  points$ = new BehaviorSubject<PointWithLabel[]>([]);

  directionsResults$: Observable<google.maps.DirectionsResult | undefined>;

  routeLength$: Observable<number>;
  routePartsLength$: Observable<RoutePartDetails[]>;

  pointsWithMarkers$ = this.points$.pipe(
    this.googleMapService.withPinElementConstructor.bind(this.googleMapService),
    map(([points, PinElement]) => {
      return points.map(point => {
        const coords = point.coords;
        return {
          position: { lat: coords[0], lng: coords[1] },
          markerOptions: {
            gmpDraggable: false,
            content: new PinElement(
              this.googleMapService.getSecondaryPinElementOptions(point.label),
            ).element,
          },
        };
      });
    }),
  );

  constructor(
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<
      google.maps.LatLngLiteral,
      RoutePreviewInputData
    >,
    private readonly fb: FormBuilder,
    protected readonly googleMapService: GoogleMapService,
  ) {}

  getRouteLength(
    directionsResults$: Observable<google.maps.DirectionsResult | undefined>,
  ) {
    return directionsResults$.pipe(
      map(result => {
        return this.googleMapService.getFullRouteLength(result?.routes ?? []);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  getRoutePartsLength(
    directionsResults$: Observable<google.maps.DirectionsResult | undefined>,
    validPoints: PointWithLabel[],
  ): Observable<RoutePartDetails[]> {
    return directionsResults$.pipe(
      map(result => {
        return this.googleMapService.getRoutePartsLength(
          result?.routes ?? [],
          validPoints,
        );
      }),
    );
  }

  get points() {
    return this.context.data.routes;
  }

  ngOnInit() {
    if (this.points.length >= 2) {
      this.points$.next(this.points);
      this.directionsResults$ = this.googleMapService.getDirectionsResult(
        this.points.map(p => p.coords),
      );

      this.routeLength$ = this.getRouteLength(this.directionsResults$);
      this.routePartsLength$ = this.getRoutePartsLength(
        this.directionsResults$,
        this.points,
      );
    }
  }
}
